import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import ContextMenu from '@/components/ContextMenu/ContextMenu';

import styles from './ActionsButton.module.scss';

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  variant?: 'danger';
  icon?: React.ReactNode;
}

interface ActionsButtonProps {
  items: ActionMenuItem[];
  ariaLabel?: string;
  variant?: 'dark' | 'lite';
  className?: string;
  /** Replaces the default horizontal ellipsis glyph; inherits the button's colour. */
  icon?: React.ReactNode;
}

export const ActionsButton = ({
  items,
  ariaLabel = 'Actions',
  variant,
  className,
  icon,
}: ActionsButtonProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleToggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen((prev) => !prev);
    setMenuAnchorEl(buttonRef.current);
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    setMenuAnchorEl(null);
  };

  useEffect(() => {
    if (!isMenuOpen) return;
    window.addEventListener('scroll', handleCloseMenu, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', handleCloseMenu, { capture: true });
  }, [isMenuOpen]);

  return (
    <div className={styles.actionsContainer} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        ref={buttonRef}
        className={clsx(
          styles.actionButton,
          variant === 'lite' && styles.actionButtonLite,
          className,
        )}
        onClick={handleToggleMenu}
        aria-label={ariaLabel}
      >
        <span className={clsx(styles.actionDots, variant === 'lite' && styles.actionDotsLite)}>
          {icon ?? '⋯'}
        </span>
      </button>
      <ContextMenu
        items={items}
        isOpen={isMenuOpen}
        onClose={handleCloseMenu}
        anchorEl={menuAnchorEl}
      />
    </div>
  );
};

export default ActionsButton;
